import React, { useLayoutEffect, useState } from 'react';
import { Box, Portal } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  shield: {
    position: 'fixed',
    inset: 0,
    zIndex: theme.zIndex.tooltip + 1,
    touchAction: 'none',
    cursor: 'default',
  },
}));

/** Intercept outside gestures and hover while keeping the conversation reachable. */
export const ChatComposerFocusGuard: React.FC<{
  focused: boolean;
  composer: HTMLDivElement | null;
}> = ({ focused, composer }) => {
  const { classes } = useStyles();
  const [bounds, setBounds] = useState<DOMRect[]>([]);
  const [dismissing, setDismissing] = useState(false);
  useLayoutEffect(() => {
    if (!focused || !composer) return;
    const frame = composer.closest('[data-chat-window]');
    const update = () => {
      setBounds([(frame ?? composer).getBoundingClientRect()]);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(composer);
    if (frame) observer.observe(frame);
    window.addEventListener('resize', update);
    document.addEventListener('scroll', update, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      document.removeEventListener('scroll', update, true);
    };
  }, [focused, composer]);

  if ((!focused && !dismissing) || !bounds.length) return null;
  // The even-odd hole lets the conversation receive clicks and scroll gestures,
  // regardless of the stacking context its panel lives in.
  const holes = bounds
    .map(
      (rect) => `0 0, ${rect.left}px ${rect.top}px,
    ${rect.left}px ${rect.bottom}px, ${rect.right}px ${rect.bottom}px,
    ${rect.right}px ${rect.top}px, ${rect.left}px ${rect.top}px, 0 0`,
    )
    .join(',');
  const clipPath = `polygon(evenodd, 0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${holes})`;
  return (
    <Portal>
      <Box
        data-chat-focus-guard
        aria-hidden="true"
        className={classes.shield}
        style={{ clipPath }}
        onPointerDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDismissing(true);
          const active = document.activeElement;
          if (active instanceof HTMLElement && composer?.contains(active))
            active.blur();
        }}
        onPointerUp={(event) => event.stopPropagation()}
        onPointerCancel={(event) => {
          event.stopPropagation();
          setDismissing(false);
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDismissing(false);
        }}
        onDoubleClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onContextMenu={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDismissing(false);
        }}
      />
    </Portal>
  );
};
